import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { consume } from "@lit/context";
import { configContext } from "../contexts";
import type { RatingConfig, Theme } from "../models";

/**
 * Application header with title, subtitle, and theme switcher.
 * @fires theme-changed - When a new theme is selected (detail: { theme: Theme }).
 */
@customElement("bab-app-header")
export class BabAppHeader extends LitElement {
  static styles = css`
    :host {
      display: block;
      margin-bottom: 24px;
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    h1 {
      margin: 0 0 2px 0;
      font-size: 1.75rem;
      font-weight: var(--bab-font-weight, 600);
      letter-spacing: -0.02em;
      color: var(--bab-text);
    }

    .subtitle {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--bab-text-secondary);
    }

    .theme-switcher {
      display: flex;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      padding: 3px;
      gap: 2px;
    }

    .theme-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      border-radius: 50%;
      cursor: pointer;
      color: var(--bab-text-secondary);
      transition: background 0.2s, color 0.2s;
    }

    .theme-btn:hover {
      background: var(--bab-bg);
      color: var(--bab-text);
    }

    .theme-btn--active {
      background: var(--bab-accent, #4a90e2);
      color: #fff !important;
    }

    .theme-btn svg {
      width: 18px;
      height: 18px;
    }
  `;

  @consume({ context: configContext, subscribe: true })
  @state()
  config!: RatingConfig;

  @property({ type: String })
  theme: Theme = "system";

  private _setTheme(theme: Theme) {
    this.dispatchEvent(new CustomEvent("theme-changed", { detail: { theme } }));
  }

  render() {
    return html`
      <header>
        <div>
          <h1>${this.config?.title ?? "BAB Chart"}</h1>
          <p class="subtitle">
            ${this.config?.subtitle ??
            `Rate ${this.config?.attributeTermPlural || "skills"} on a 1–5 scale and share the results.`}
          </p>
        </div>
        <div class="theme-switcher">
          <button
            class="theme-btn ${this.theme === "light" ? "theme-btn--active" : ""}"
            title="Light theme"
            @click="${() => this._setTheme("light")}"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          </button>
          <button
            class="theme-btn ${this.theme === "dark" ? "theme-btn--active" : ""}"
            title="Dark theme"
            @click="${() => this._setTheme("dark")}"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          </button>
          <button
            class="theme-btn ${this.theme === "system" ? "theme-btn--active" : ""}"
            title="Follow system theme"
            @click="${() => this._setTheme("system")}"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
          </button>
        </div>
      </header>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-app-header": BabAppHeader;
  }
}
